import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import userDetail from "Stores/userDetail";
import { toasterState } from "Components/common/toaster";
import { outboxStoreData } from "../outboxStore";
import { normalizeConversations } from "./inboxV2Utils";

class InboxV2Store {
  @observable selectedContact = null;
  @observable feed = [];
  @observable openConversationId = "";
  @observable conversationsLoading = false;
  @observable taggedSequences = [];
  @observable sequencesLoading = false;
  @observable resumingSeqId = "";

  // LinkedIn thread: fetched from the access-controlled liReply endpoint, not /conversations,
  // so owner/permission/connection state matches the existing LinkedInReplyContentPopup.
  @observable liReply = {
    messages: [],
    canViewLiReplies: true,
    isViewerOwner: false,
    ownerName: "",
    ownerProfileUrl: "",
    profilePictureUrl: "",
    liConnected: true
  };
  @observable liReplyLoading = false;
  @observable liSending = false;

  @action
  setLiReply = (value = {}) => {
    this.liReply = { ...this.liReply, ...value };
  };

  @action
  resetLiReply = () => {
    this.liReply = {
      messages: [],
      canViewLiReplies: true,
      ownerName: "",
      ownerProfileUrl: "",
      profilePictureUrl: "",
      liConnected: true
    };
  };

  @action
  setOpenConversationId = value => {
    this.openConversationId = value;
  };

  // Opening a conversation marks its emails read: optimistic local update + backend, so
  // the bold/closed-envelope styling clears immediately.
  @action
  openConversation = (item = {}) => {
    this.openConversationId = item?.id || "";
    if (item?.unread) this.markConversationRead(item);
  };

  @action
  markConversationRead = async (item = {}) => {
    const taskIds = item?.taskIds || [];
    this.feed = this.feed.map(entry =>
      entry?.id === item?.id ? { ...entry, unread: false } : entry
    );
    if (!taskIds.length) return;
    try {
      await makeApi({
        url: URLS.updateOutboxMailReadStatus,
        method: "POST",
        data: { status: "READ", tasks: taskIds, totalOutboxTasks: taskIds.length }
      });
      outboxStoreData.getOutboxUnreadCount();
    } catch (err) {
      console.error("Error marking inbox v2 conversation read", err);
    }
  };

  // Closing a thread drops back to the full-width contact list, as in the design.
  @action
  clearSelection = () => {
    this.selectedContact = null;
    this.feed = [];
    this.openConversationId = "";
    this.taggedSequences = [];
    this.resetLiReply();
  };

  @action
  reset = () => {
    this.selectedContact = null;
    this.feed = [];
    this.openConversationId = "";
    this.conversationsLoading = false;
    this.taggedSequences = [];
    this.sequencesLoading = false;
    this.resumingSeqId = "";
    this.resetLiReply();
  };

  getContactId = (item = {}) =>
    item?.contact?.[0]?.id || item?.task?.contactId || "";

  @action
  selectContact = (item = {}) => {
    const contactId = this.getContactId(item);
    if (!contactId) return;
    this.selectedContact = item;
    this.feed = [];
    this.openConversationId = "";
    this.taggedSequences = [];
    this.getConversations(contactId);
    this.getContactSequences(contactId);
  };

  // silent = refresh the feed without the full-pane skeleton (used after sending a reply,
  // so only the thread updates -- not the whole conversations pane).
  @action
  getConversations = async (contactId = "", { silent = false } = {}) => {
    if (!contactId) return;
    if (!silent) this.conversationsLoading = true;
    try {
      const config = {
        url: URLS.outboxConversations.replace("<<contactId>>", contactId),
        method: "POST"
      };
      const res = await makeApi(config);
      // Body is null when the contact has no tasks at all.
      const feed = normalizeConversations(
        res?.data || null,
        userDetail?.connectedInboxes || []
      );
      this.setFeed(feed);
    } catch (err) {
      console.error("Error in getting inbox v2 conversations", err);
      this.setFeed([]);
    }
    if (!silent) this.setConversationsLoading(false);
  };

  @action
  setFeed = (feed = []) => {
    this.feed = feed;
    // Keep the open conversation across a refresh (e.g. after sending a reply); only
    // fall back to the first when the previous one is gone.
    const stillOpen = feed.some(item => item?.id === this.openConversationId);
    this.openConversationId = stillOpen
      ? this.openConversationId
      : feed?.[0]?.id || "";
    // The auto-opened conversation is on screen, so mark it read too.
    const open = feed.find(item => item?.id === this.openConversationId);
    if (open?.unread) this.markConversationRead(open);
  };

  @action
  setConversationsLoading = value => {
    this.conversationsLoading = value;
  };

  @action
  setTaggedSequences = (value = []) => {
    this.taggedSequences = value;
  };

  @action
  getContactSequences = async (contactId = "") => {
    if (!contactId) return;
    this.sequencesLoading = true;
    try {
      const config = {
        url: URLS.getContactDetail.replace("<<contactId>>", contactId),
        method: "GET"
      };
      const res = await makeApi(config);
      this.setTaggedSequences(res?.data?.contact?.taggedSequences || []);
    } catch (err) {
      console.error("Error in getting contact sequences", err);
      this.setTaggedSequences([]);
    }
    this.setSequencesLoading(false);
  };

  @action
  setSequencesLoading = value => {
    this.sequencesLoading = value;
  };

  @action
  setResumingSeqId = value => {
    this.resumingSeqId = value;
  };

  @action
  setLiReplyLoading = value => {
    this.liReplyLoading = value;
  };

  @action
  setLiSending = value => {
    this.liSending = value;
  };

  // Mirrors OutboxStore.getLinkedInReplyForActivity but stores inline (no popup). Returns
  // messages + the owner/permission/connection flags that gate replying.
  @action
  getLinkedInReply = async (contactId = "", accountId = "") => {
    if (!contactId) return;
    this.liReplyLoading = true;
    this.resetLiReply();
    try {
      const query = accountId ? `?accountId=${accountId}` : "";
      const res = await makeApi({
        url: `${URLS.getLinkedInReply}/${contactId}${query}`,
        method: "GET"
      });
      const data = res?.data || {};
      if (Object.keys(data).length > 0) {
        const {
          messages = [],
          canViewLiReplies = true,
          isViewerOwner = false,
          ownerName = "",
          ownerProfileUrl = "",
          profilePictureUrl = "",
          liConnected = true
        } = data;
        this.setLiReply({
          messages,
          canViewLiReplies,
          isViewerOwner,
          ownerName,
          ownerProfileUrl,
          profilePictureUrl,
          liConnected
        });
      }
    } catch (err) {
      console.error("Error getting inbox v2 linkedin reply", err);
    }
    this.setLiReplyLoading(false);
  };

  // Send order mirrors LinkedInReplyContentPopup: ssId (scheduled sequence) when present,
  // else cid (contact). Same response-code handling; the sent message is prepended.
  @action
  sendLinkedInReply = async (text = "") => {
    if (this.liSending || !text?.trim()) return false;
    const contactId = this.getContactId(this.selectedContact || {});
    const scheduledSeqId = this.selectedContact?.scheduledSequenceId || "";
    if (!scheduledSeqId && !contactId) return false;

    this.setLiSending(true);
    let sent = false;
    try {
      const timeZone = Utils.getCurrentUserTimeZone();
      const dueDateString = `${Utils.createScheduleDate(new Date())}${
        timeZone?.replace("UTC", "") || ""
      }`;
      const data = scheduledSeqId
        ? { ssId: scheduledSeqId, text, dueDateString }
        : { cid: contactId, text, dueDateString };
      const res = await makeApi({
        url: URLS.sendLinkedInMessage,
        method: "POST",
        data
      });
      const respData = res?.data || {};
      const responseCode = +respData?.responseCode;
      if (responseCode === 200) {
        this.setLiReply({
          messages: [
            {
              senderName: respData.senderName,
              updateTs: respData.dueDate,
              imageUrl: respData.profileImage,
              message: respData.text
            },
            ...(this.liReply?.messages || [])
          ]
        });
        toasterState.setToastMsg("Message sent", "success");
        sent = true;
      } else if (responseCode === 100) {
        toasterState.setToastMsg(
          "No active LinkedIn connection found. Please connect and try again.",
          "failure"
        );
      } else if (responseCode === 101) {
        toasterState.setToastMsg(
          "You cannot send a message to this contact. Please contact your admin.",
          "failure"
        );
      } else if (responseCode === 102) {
        toasterState.setToastMsg(
          "This contact can no longer receive messages from you.",
          "failure"
        );
      } else if (responseCode === 103) {
        toasterState.setToastMsg(
          "You've reached the LinkedIn daily message limit. Please try sending later.",
          "failure"
        );
      } else {
        Utils.showApiFailureResponse(respData);
      }
    } catch (err) {
      console.error("Error sending inbox v2 linkedin reply", err);
    }
    this.setLiSending(false);
    return sent;
  };

  /*
   * Resumes one contact in one sequence -- a scheduledSeqId is a single contact's
   * enrolment, so this never touches other contacts. Response handling mirrors
   * Components/ContactCard/SequenceList.js handleResumeContact.
   */
  @action
  resumeSequence = async (scheduledSeqId = "") => {
    const contactId = this.getContactId(this.selectedContact || {});
    if (!scheduledSeqId || !contactId) return;
    this.setResumingSeqId(scheduledSeqId);
    try {
      const config = {
        url: URLS.resumeContactSeq.replace("<<seqId>>", scheduledSeqId),
        method: "PUT"
      };
      const res = await makeApi(config);
      if (res?.data) {
        const { inboxError = {}, respCode = "" } = res?.data || {};
        const hasInboxError = inboxError && Object.keys(inboxError)?.length > 0;
        if (+respCode === 200 && !hasInboxError) {
          toasterState.setToastMsg(
            "Contact has been activated successfully",
            "success"
          );
          await this.getContactSequences(contactId);
        } else if (+respCode === 407) {
          toasterState.setToastMsg(
            "Failed to resume contact. Contact's scheduled inbox is unavailable.",
            "failure"
          );
        } else if (+respCode === 403) {
          toasterState.setToastMsg(
            "Failed to resume contact. You don't have access to this contact.",
            "failure"
          );
        } else if (hasInboxError) {
          toasterState.setToastMsg(
            `Failed to activate the contact as ${
              Object.keys(inboxError)?.[0]
            } is not connected to this sequence.`,
            "failure"
          );
        } else {
          toasterState.setToastMsg(
            "Failed to resume contact from the sequence",
            "failure"
          );
        }
      } else {
        Utils.showApiFailureResponse(res);
      }
    } catch (err) {
      console.error("Error in resuming contact sequence", err);
      toasterState.setToastMsg(
        "Failed to resume contact from the sequence",
        "failure"
      );
    }
    this.setResumingSeqId("");
  };
}

const inboxV2Store = new InboxV2Store();
export { InboxV2Store, inboxV2Store };
export default inboxV2Store;
